export interface ColorSet {
	background: LightDarkColorEntry;
	border: LightDarkColorEntry;
	text: LightDarkColorEntry;
	hex: string;
}

export interface LightDarkColorEntry {
	light: string;
	dark: string;
}

export interface ColorTheme {
	primary: ColorSet;
	secondary: ColorSet;
	text: LightDarkColorEntry;
	placeholdertext: LightDarkColorEntry;
	accent: LightDarkColorEntry;
}

export interface TextTipLevelEntry {
	value: number;
	color: ColorTheme;
	limit: number;
}

const color_text_light = { light: 'text-white', dark: 'dark:text-white' };
const color_placeholder_light = {
	light: 'placeholder-surface-50',
	dark: 'dark:placeholder-surface-50'
};
const color_text_dark = { light: 'text-black', dark: 'dark:text-black' };
const color_placeholder_dark = {
	light: 'placeholder-surface-900',
	dark: 'dark:placeholder-surface-900'
};

export const stBrandBlue = '#2191C1';
export const stBrandPink = '#D946EF';

const tipTierFree = '#3B82F6'; // overlay free tip color specifically
export const tipTierCommon = '#4DD0E1'; // <$5 blue
export const tipTierUncommon = '#6EE7B7'; // green
export const tipTierMagic = '#FFEE58'; // yellow
export const tipTierRare = '#FF9800'; // orange
export const tipTierEpic = '#E11D48'; // purple
export const tipTierLegendary = '#E53935'; // red
export const tipTierMythic = '#424242'; // dark grey

function colorTheme(
	hex: string,
	textColor?: LightDarkColorEntry,
	placeholderTextColor?: LightDarkColorEntry
): ColorTheme {
	return {
		primary: {
			hex: hex,
			background: { light: `bg-[${hex}]`, dark: `dark:bg-[${hex}]` },
			border: { light: `border-[${hex}]`, dark: `dark:border-[${hex}]` },
			text: { light: `text-[${hex}]`, dark: `dark:text-[${hex}]` }
		},
		secondary: {
			hex: hex,
			background: { light: `bg-[${hex}]`, dark: `dark:bg-[${hex}]` },
			border: { light: `border-[${hex}]`, dark: `dark:border-[${hex}]` },
			text: { light: `text-[${hex}]`, dark: `dark:text-[${hex}]` }
		},
		text: textColor || color_text_light,
		placeholdertext: placeholderTextColor || color_placeholder_light,
		accent: { light: `bg-[${hex}]`, dark: `dark:bg-[${hex}]` }
	};
}

export const textTipLookup: Array<TextTipLevelEntry> = [
	{
		value: 1.0,
		color: colorTheme(tipTierFree),
		limit: 0
	},
	{
		value: 2.0,
		color: colorTheme(tipTierCommon),
		limit: 50
	},
	{
		value: 5.0,
		color: colorTheme(tipTierUncommon, color_text_dark, color_placeholder_dark),
		limit: 150
	},
	{
		value: 10.0,
		color: colorTheme(tipTierMagic, color_text_dark, color_placeholder_dark),
		limit: 200
	},
	{
		value: 20.0,
		color: colorTheme(tipTierRare),
		limit: 225
	},
	{
		value: 50.0,
		color: colorTheme(tipTierEpic),
		limit: 250
	},
	{
		value: 100.0,
		color: colorTheme(tipTierLegendary),
		limit: 270
	},
	{
		value: 200.0,
		color: colorTheme(tipTierLegendary),
		limit: 290
	},
	{
		value: 300.0,
		color: colorTheme(tipTierLegendary),
		limit: 310
	},
	{
		value: 400.0,
		color: colorTheme(tipTierLegendary),
		limit: 330
	},
	{
		value: 500.0,
		color: colorTheme(tipTierLegendary),
		limit: 350
	},
	{
		value: 1000.0,
		color: colorTheme(tipTierMythic),
		limit: 500
	}
];

export function findTextTipBand(input: number): number {
	const cents = Math.round((input % 1) * 100);
	const adjustedInput = cents === 99 ? Math.ceil(input) : input;
	let position = 0;
	for (let i = 0; i < Object.keys(textTipLookup).length; i++) {
		if (adjustedInput >= textTipLookup[i].value) {
			position = i;
		}
	}
	return position;
}

export function findOverlayTextTipBand(input: number): number {
	let position = 0;
	for (let i = 0; i < Object.keys(overlayTextTipLookup).length; i++) {
		if (input >= overlayTextTipLookup[i].value) {
			position = i;
		}
	}
	return position;
}

const overlay_red = {
	primary: {
		hex: '#e53935',
		background: { light: 'bg-red-600', dark: 'dark:bg-red-600' },
		border: { light: 'border-red-600', dark: 'dark:border-red-600' },
		text: { light: 'text-red-600', dark: 'dark:text-red-600' }
	},
	secondary: {
		hex: '#d32f2f',
		background: { light: 'bg-red-700', dark: 'dark:bg-red-700' },
		border: { light: 'border-red-700', dark: 'dark:border-red-700' },
		text: { light: 'text-red-700', dark: 'dark:text-red-700' }
	},
	text: color_text_light,
	placeholdertext: color_text_light,
	accent: { light: 'accent-red-600', dark: 'dark:accent-red-600' }
};

export const overlayTextTipLookup: Array<TextTipLevelEntry> = [
	{
		value: 1.0,
		color: {
			primary: {
				hex: '#2196f3',
				background: { light: 'bg-blue-500', dark: 'dark:bg-blue-500' },
				border: { light: 'border-blue-500', dark: 'dark:border-blue-500' },
				text: { light: 'text-blue-500', dark: 'dark:text-blue-500' }
			},
			secondary: {
				hex: '#2196f3',
				background: { light: 'bg-blue-700', dark: 'dark:bg-blue-700' },
				border: { light: 'border-blue-700', dark: 'dark:border-blue-700' },
				text: { light: 'text-blue-700', dark: 'dark:text-blue-700' }
			},
			text: color_text_light,
			placeholdertext: color_placeholder_light,
			accent: { light: 'accent-blue-500', dark: 'dark:accent-blue-500' }
		},
		limit: 0
	},
	{
		value: 2.0,
		color: {
			primary: {
				hex: '#4dd0e1',
				background: { light: 'bg-cyan-300', dark: 'dark:bg-cyan-300' },
				border: { light: 'border-cyan-300', dark: 'dark:border-cyan-300' },
				text: { light: 'text-cyan-300', dark: 'dark:text-cyan-300' }
			},
			secondary: {
				hex: '#00bcd4',
				background: { light: 'bg-cyan-500', dark: 'dark:bg-cyan-500' },
				border: { light: 'border-cyan-500', dark: 'dark:border-cyan-500' },
				text: { light: 'text-cyan-500', dark: 'dark:text-cyan-500' }
			},
			text: color_text_dark,
			placeholdertext: color_placeholder_dark,
			accent: { light: 'accent-cyan-300', dark: 'dark:accent-cyan-300' }
		},
		limit: 50
	},
	{
		value: 5.0,
		color: {
			primary: {
				hex: '#6ee7b7',
				background: { light: 'bg-emerald-300', dark: 'dark:bg-emerald-300' },
				border: { light: 'border-emerald-300', dark: 'dark:border-emerald-300' },
				text: { light: 'text-emerald-300', dark: 'dark:text-emerald-300' }
			},
			secondary: {
				hex: '#009688',
				background: { light: 'bg-teal-500', dark: 'dark:bg-teal-500' },
				border: { light: 'border-teal-500', dark: 'dark:border-teal-500' },
				text: { light: 'text-teal-500', dark: 'dark:text-teal-500' }
			},
			text: color_text_dark,
			placeholdertext: color_placeholder_dark,
			accent: { light: 'accent-emerald-300', dark: 'dark:accent-emerald-300' }
		},
		limit: 150
	},
	{
		value: 10.0,
		color: {
			primary: {
				hex: '#ffee58',
				background: { light: 'bg-yellow-400', dark: 'dark:bg-yellow-400' },
				border: { light: 'border-yellow-400', dark: 'dark:border-yellow-400' },
				text: { light: 'text-yellow-400', dark: 'dark:text-yellow-400' }
			},
			secondary: {
				hex: '#ffca28',
				background: { light: 'bg-amber-400', dark: 'dark:bg-amber-400' },
				border: { light: 'border-amber-400', dark: 'dark:border-amber-400' },
				text: { light: 'text-amber-400', dark: 'dark:text-amber-400' }
			},
			text: color_text_dark,
			placeholdertext: color_placeholder_dark,
			accent: { light: 'accent-yellow-400', dark: 'dark:accent-yellow-400' }
		},
		limit: 200
	},
	{
		value: 20.0,
		color: {
			primary: {
				hex: '#ff9800',
				background: { light: 'bg-orange-500', dark: 'dark:bg-orange-500' },
				border: { light: 'border-orange-500', dark: 'dark:border-orange-500' },
				text: { light: 'text-orange-500', dark: 'dark:text-orange-500' }
			},
			secondary: {
				hex: '#fb8c00',
				background: { light: 'bg-orange-600', dark: 'dark:bg-orange-600' },
				border: { light: 'border-orange-600', dark: 'dark:border-orange-600' },
				text: { light: 'text-orange-600', dark: 'dark:text-orange-600' }
			},
			text: color_text_light,
			placeholdertext: color_placeholder_light,
			accent: { light: 'accent-orange-500', dark: 'dark:accent-orange-500' }
		},
		limit: 225
	},
	{
		value: 50.0,
		color: {
			primary: {
				hex: '#e11d48',
				background: { light: 'bg-rose-600', dark: 'dark:bg-rose-600' },
				border: { light: 'border-rose-600', dark: 'dark:border-rose-600' },
				text: { light: 'text-rose-600', dark: 'dark:text-rose-600' }
			},
			secondary: {
				hex: '#c2185b',
				background: { light: 'bg-pink-700', dark: 'dark:bg-pink-700' },
				border: { light: 'border-pink-700', dark: 'dark:border-pink-700' },
				text: { light: 'text-pink-700', dark: 'dark:text-pink-700' }
			},
			text: color_text_light,
			placeholdertext: color_placeholder_light,
			accent: { light: 'accent-rose-600', dark: 'dark:accent-rose-600' }
		},
		limit: 250
	},
	{
		value: 100.0,
		color: overlay_red,
		limit: 270
	},
	{
		value: 200.0,
		color: overlay_red,
		limit: 290
	},
	{
		value: 300.0,
		color: overlay_red,
		limit: 310
	},
	{
		value: 400.0,
		color: overlay_red,
		limit: 330
	},
	{
		value: 500.0,
		color: overlay_red,
		limit: 350
	},
	{
		value: 1000.0,
		color: {
			primary: {
				hex: '#424242',
				background: { light: 'bg-gray-800', dark: 'dark:bg-gray-800' },
				border: { light: 'border-gray-800', dark: 'dark:border-gray-800' },
				text: { light: 'text-gray-800', dark: 'dark:text-gray-800' }
			},
			secondary: {
				hex: '#616161',
				background: { light: 'bg-gray-700', dark: 'dark:bg-gray-700' },
				border: { light: 'border-gray-700', dark: 'dark:border-gray-700' },
				text: { light: 'text-gray-700', dark: 'dark:text-gray-700' }
			},
			text: {
				light: "text-yellow-300 font-['Playfair_Display']",
				dark: "dark:text-yellow-300 dark:font-['Playfair_Display']"
			},
			placeholdertext: { light: 'placeholder-yellow-300', dark: 'dark:placeholder-yellow-300' },
			accent: { light: 'accent-gray-800', dark: 'dark:accent-gray-800' }
		},
		limit: 500
	}
];
